import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const SuperAdminStudentManagement = ({ showActions }) => {
  const { user } = useAuth();
  const canManageStudents = typeof showActions === 'boolean'
    ? showActions
    : user?.role === 'SUPER_ADMIN' || user?.role === 'EXAM_SECTION' || user?.role === 'HOD';
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Password reset states
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Filters
  const [departmentFilter, setDepartmentFilter] = useState('ALL');
  const [semesterFilter, setSemesterFilter] = useState('ALL');
  const [sectionFilter, setSectionFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState('ALL');
  const [phoneVerifiedFilter, setPhoneVerifiedFilter] = useState('ALL');
  
  // NOT toggles
  const [notDepartment, setNotDepartment] = useState(false);
  const [notSemester, setNotSemester] = useState(false);
  const [notSection, setNotSection] = useState(false);
  const [notStatus, setNotStatus] = useState(false);

  // Bulk update selections
  const [bulkDepartment, setBulkDepartment] = useState('NO_CHANGE');
  const [bulkSemester, setBulkSemester] = useState('NO_CHANGE');
  const [bulkSection, setBulkSection] = useState('NO_CHANGE');
  const [bulkStatus, setBulkStatus] = useState('NO_CHANGE');
  const [bulkEmailVerified, setBulkEmailVerified] = useState('NO_CHANGE');
  const [bulkPhoneVerified, setBulkPhoneVerified] = useState('NO_CHANGE');
  
  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [lastLoginFrom, setLastLoginFrom] = useState('');
  const [lastLoginTo, setLastLoginTo] = useState('');
  
  // Sorting
  const [sortBy, setSortBy] = useState('sem-asc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  useEffect(() => {
    if (user?.role === 'SUPER_ADMIN' || user?.role === 'EXAM_SECTION' || user?.role === 'HOD' || user?.role === 'FACULTY') {
      fetchStudents();
    }
  }, [user]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/students/all', {
        params: { includeInactive: true }
      });
      
      if (response.data.success) {
        setStudents(response.data.data || []);
      } else {
        toast.error('Failed to fetch students');
      }
    } catch (error) {
      console.error('Fetch students error:', error);
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredStudents = () => {
    return students.filter(student => {
      // Department filter with NOT
      if (departmentFilter !== 'ALL') {
        const matches = student.department === departmentFilter;
        if (notDepartment ? matches : !matches) return false;
      }
      
      // Semester filter with NOT
      if (semesterFilter !== 'ALL') {
        const matches = student.semester === semesterFilter;
        if (notSemester ? matches : !matches) return false;
      }

      // Section filter with NOT
      if (sectionFilter !== 'ALL') {
        const matches = sectionFilter === 'N/A'
          ? !student.section || student.section === 'N/A'
          : student.section === sectionFilter;
        if (notSection ? matches : !matches) return false;
      }
      
      // Status filter with NOT
      if (statusFilter !== 'ALL') {
        const matches = student.status === statusFilter;
        if (notStatus ? matches : !matches) return false;
      }
      
      // Email verified filter
      if (emailVerifiedFilter !== 'ALL') {
        const isVerified = student.isEmailVerified === true;
        if (emailVerifiedFilter === 'VERIFIED' && !isVerified) return false;
        if (emailVerifiedFilter === 'NOT_VERIFIED' && isVerified) return false;
      }
      
      // Phone verified filter
      if (phoneVerifiedFilter !== 'ALL') {
        const isVerified = student.isPhoneVerified === true;
        if (phoneVerifiedFilter === 'VERIFIED' && !isVerified) return false;
        if (phoneVerifiedFilter === 'NOT_VERIFIED' && isVerified) return false;
      }
      
      // Date range filter (registration date)
      if (dateFrom) {
        const studentDate = new Date(student.createdAt);
        const fromDate = new Date(dateFrom);
        if (studentDate < fromDate) return false;
      }
      
      if (dateTo) {
        const studentDate = new Date(student.createdAt);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (studentDate > toDate) return false;
      }
      
      // Last login filter
      if (lastLoginFrom && student.lastLoginAt) {
        const loginDate = new Date(student.lastLoginAt);
        const fromDate = new Date(lastLoginFrom);
        if (loginDate < fromDate) return false;
      }
      
      if (lastLoginTo && student.lastLoginAt) {
        const loginDate = new Date(student.lastLoginAt);
        const toDate = new Date(lastLoginTo);
        toDate.setHours(23, 59, 59, 999);
        if (loginDate > toDate) return false;
      }
      
      // Search filter
      if (searchTerm.trim()) {
        const search = searchTerm.toLowerCase();
        const matchesUSN = student.usn?.toLowerCase().includes(search);
        const matchesName = student.name?.toLowerCase().includes(search);
        const matchesEmail = student.email?.toLowerCase().includes(search);
        const matchesPhone = student.phone?.toLowerCase().includes(search);
        
        if (!matchesUSN && !matchesName && !matchesEmail && !matchesPhone) return false;
      }
      
      return true;
    });
  };

  const getSortedStudents = () => {
    const filtered = getFilteredStudents();
    
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'usn-asc':
          return (a.usn || '').localeCompare(b.usn || '');
        case 'usn-desc':
          return (b.usn || '').localeCompare(a.usn || '');
        case 'name-asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name-desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'date-asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'date-desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'dept-asc':
          return (a.department || '').localeCompare(b.department || '');
        case 'dept-desc':
          return (b.department || '').localeCompare(a.department || '');
        case 'sem-asc':
          return (a.semester || '').localeCompare(b.semester || '');
        case 'sem-desc':
          return (b.semester || '').localeCompare(a.semester || '');
        case 'sec-asc':
          return (a.section || '').localeCompare(b.section || '');
        case 'sec-desc':
          return (b.section || '').localeCompare(a.section || '');
        default:
          return 0;
      }
    });
  };

  const getPaginatedStudents = () => {
    const sorted = getSortedStudents();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sorted.slice(startIndex, startIndex + itemsPerPage);
  };

  const clearAllFilters = () => {
    setDepartmentFilter('ALL');
    setSemesterFilter('ALL');
    setSectionFilter('ALL');
    setStatusFilter('ALL');
    setEmailVerifiedFilter('ALL');
    setPhoneVerifiedFilter('ALL');
    setNotDepartment(false);
    setNotSemester(false);
    setNotSection(false);
    setNotStatus(false);
    setDateFrom('');
    setDateTo('');
    setLastLoginFrom('');
    setLastLoginTo('');
    setSearchTerm('');
  };

  const hasActiveFilters = () => {
    return departmentFilter !== 'ALL' ||
           semesterFilter !== 'ALL' ||
          sectionFilter !== 'ALL' ||
           statusFilter !== 'ALL' ||
           emailVerifiedFilter !== 'ALL' ||
           phoneVerifiedFilter !== 'ALL' ||
           dateFrom !== '' ||
           dateTo !== '' ||
           lastLoginFrom !== '' ||
           lastLoginTo !== '' ||
           searchTerm !== '';
  };

  const handleSelectAll = () => {
    const paginated = getPaginatedStudents();
    if (selectAll) {
      setSelectedStudents(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(paginated.map(s => s.id));
      setSelectedStudents(allIds);
      setSelectAll(true);
    }
  };

  const handleSelectStudent = (studentId) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
    setSelectAll(false);
  };

  const handleEditStudent = (student) => {
    setEditingStudent({ ...student });
    setShowEditModal(true);
    setShowResetPassword(false);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSaveStudent = async () => {
    try {
      const response = await api.put(`/users/students/${editingStudent.id}`, {
        name: editingStudent.name,
        usn: editingStudent.usn,
        collegeEmail: editingStudent.email,
        phoneNumber: editingStudent.phone,
        department: editingStudent.department,
        semester: editingStudent.semester,
        section: editingStudent.section,
        isActive: editingStudent.status === 'ACTIVE',
        isEmailVerified: editingStudent.isEmailVerified,
        isPhoneVerified: editingStudent.isPhoneVerified
      });
      
      if (response.data.success) {
        toast.success('Student updated successfully');
        setShowEditModal(false);
        fetchStudents();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update student');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please enter both password fields');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setResettingPassword(true);
      const response = await api.post(`/users/students/${editingStudent.id}/reset-password`, {
        newPassword
      });

      if (response.data.success) {
        toast.success('Password reset successfully');
        setShowResetPassword(false);
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/users/students/${studentId}`);
      toast.success('Student deleted successfully');
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete student');
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedStudents.size === 0) {
      toast.error('No students selected');
      return;
    }

    const updateData = {};
    if (bulkDepartment !== 'NO_CHANGE') updateData.department = bulkDepartment;
    if (bulkSemester !== 'NO_CHANGE') updateData.semester = bulkSemester;
    if (bulkSection !== 'NO_CHANGE') {
      updateData.section = bulkSection === 'N/A' ? null : bulkSection;
    }
    if (bulkStatus !== 'NO_CHANGE') updateData.isActive = bulkStatus === 'ACTIVE';
    if (bulkEmailVerified !== 'NO_CHANGE') updateData.isEmailVerified = bulkEmailVerified === 'VERIFIED';
    if (bulkPhoneVerified !== 'NO_CHANGE') updateData.isPhoneVerified = bulkPhoneVerified === 'VERIFIED';

    if (Object.keys(updateData).length === 0) {
      toast.error('Select at least one field to update');
      return;
    }

    if (!window.confirm(`Apply updates to ${selectedStudents.size} student(s)?`)) {
      return;
    }

    try {
      const loadingToast = toast.info(`Updating ${selectedStudents.size} student(s)...`, { autoClose: false });
      const promises = Array.from(selectedStudents).map(studentId => {
        return api.put(`/users/students/${studentId}`, updateData)
          .then(() => ({ success: true }))
          .catch(() => ({ success: false }));
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r.success).length;

      toast.dismiss(loadingToast);
      toast.success(`Updated ${successCount} student(s)`);

      setBulkDepartment('NO_CHANGE');
      setBulkSemester('NO_CHANGE');
      setBulkSection('NO_CHANGE');
      setBulkStatus('NO_CHANGE');
      setBulkEmailVerified('NO_CHANGE');
      setBulkPhoneVerified('NO_CHANGE');
      setSelectedStudents(new Set());
      setSelectAll(false);
      fetchStudents();
    } catch (error) {
      toast.error('Bulk update failed');
    }
  };

  const totalPages = Math.ceil(getSortedStudents().length / itemsPerPage);
  const hasInactiveVisible = getPaginatedStudents().some(student => student.status === 'INACTIVE');

  if (!['SUPER_ADMIN', 'EXAM_SECTION', 'HOD', 'FACULTY'].includes(user?.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h1>
          <p className="text-slate-600">This page is only accessible to authorized staff</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900">Student Database Management</h1>
          <p className="text-slate-600 mt-1">Complete student records with advanced filtering and editing</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-slate-600 mb-1">Total Students</h3>
            <p className="text-2xl font-bold text-slate-900">{students.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-slate-600 mb-1">Active</h3>
            <p className="text-2xl font-bold text-green-600">
              {students.filter(s => s.status === 'ACTIVE').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-slate-600 mb-1">Inactive</h3>
            <p className="text-2xl font-bold text-red-600">
              {students.filter(s => s.status === 'INACTIVE').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-slate-600 mb-1">Email Verified</h3>
            <p className="text-2xl font-bold text-blue-600">
              {students.filter(s => s.isEmailVerified).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-slate-600 mb-1">Departments</h3>
            <p className="text-2xl font-bold text-purple-600">
              {new Set(students.map(s => s.department)).size}
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search by USN, Name, Email, or Phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="px-4 py-2 text-slate-600 hover:text-slate-900 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          {/* Basic Filters */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
                <span>Department</span>
                {departmentFilter !== 'ALL' && (
                  <label className="flex items-center gap-1 text-xs font-normal cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notDepartment}
                      onChange={(e) => setNotDepartment(e.target.checked)}
                      className="w-3 h-3 text-red-600 border-slate-300 rounded focus:ring-red-500"
                    />
                    <span className="text-red-600">NOT</span>
                  </label>
                )}
              </label>
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary text-sm"
              >
                <option value="ALL">All</option>
                <option value="CS">CS</option>
                <option value="CD">CD</option>
                <option value="CI">CI</option>
                <option value="CY">CY</option>
                <option value="EC">EC</option>
                <option value="ME">ME</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
                <span>Semester</span>
                {semesterFilter !== 'ALL' && (
                  <label className="flex items-center gap-1 text-xs font-normal cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notSemester}
                      onChange={(e) => setNotSemester(e.target.checked)}
                      className="w-3 h-3 text-red-600 border-slate-300 rounded focus:ring-red-500"
                    />
                    <span className="text-red-600">NOT</span>
                  </label>
                )}
              </label>
              <select
                value={semesterFilter}
                onChange={(e) => setSemesterFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary text-sm"
              >
                <option value="ALL">All</option>
                {[1, 2, 3, 4, 5, 6, 7, 8].map(sem => (
                  <option key={sem} value={`SEM_${sem}`}>Semester {sem}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
                <span>Section</span>
                {sectionFilter !== 'ALL' && (
                  <label className="flex items-center gap-1 text-xs font-normal cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notSection}
                      onChange={(e) => setNotSection(e.target.checked)}
                      className="w-3 h-3 text-red-600 border-slate-300 rounded focus:ring-red-500"
                    />
                    <span className="text-red-600">NOT</span>
                  </label>
                )}
              </label>
              <select
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary text-sm"
              >
                <option value="ALL">All</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
                <option value="N/A">N/A</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
                <span>Status</span>
                {statusFilter !== 'ALL' && (
                  <label className="flex items-center gap-1 text-xs font-normal cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notStatus}
                      onChange={(e) => setNotStatus(e.target.checked)}
                      className="w-3 h-3 text-red-600 border-slate-300 rounded focus:ring-red-500"
                    />
                    <span className="text-red-600">NOT</span>
                  </label>
                )}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary text-sm"
              >
                <option value="ALL">All</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email Status</label>
              <select
                value={emailVerifiedFilter}
                onChange={(e) => setEmailVerifiedFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary text-sm"
              >
                <option value="ALL">All</option>
                <option value="VERIFIED">✓ Verified</option>
                <option value="NOT_VERIFIED">✗ Not Verified</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Phone Status</label>
              <select
                value={phoneVerifiedFilter}
                onChange={(e) => setPhoneVerifiedFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary text-sm"
              >
                <option value="ALL">All</option>
                <option value="VERIFIED">✓ Verified</option>
                <option value="NOT_VERIFIED">✗ Not Verified</option>
              </select>
            </div>
          </div>

          {/* Advanced Filters Toggle */}
          <div className="flex justify-between items-center mb-3">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="text-sm font-medium text-primary hover:text-primary-dark flex items-center gap-2"
            >
              {showAdvancedFilters ? '▼' : '▶'} Advanced Filters (Date Ranges)
            </button>
            {hasActiveFilters() && (
              <button
                onClick={clearAllFilters}
                className="text-sm font-medium text-red-600 hover:text-red-700"
              >
                Clear All Filters
              </button>
            )}
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="border-t border-slate-200 pt-4 mt-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Registered From
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Registered To
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Last Login From
                  </label>
                  <input
                    type="date"
                    value={lastLoginFrom}
                    onChange={(e) => setLastLoginFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Last Login To
                  </label>
                  <input
                    type="date"
                    value={lastLoginTo}
                    onChange={(e) => setLastLoginTo(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Filter Results Count */}
          {hasActiveFilters() && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                {getSortedStudents().length} student(s) match current filters
              </p>
            </div>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {canManageStudents && selectedStudents.size > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="font-medium text-indigo-900">
                    {selectedStudents.size} student(s) selected
                  </span>
                  <button
                    onClick={() => {
                      setSelectedStudents(new Set());
                      setSelectAll(false);
                    }}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    Clear Selection
                  </button>
                </div>
                <button
                  onClick={handleBulkUpdate}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Apply Changes
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <select
                  value={bulkDepartment}
                  onChange={(e) => setBulkDepartment(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="NO_CHANGE">Dept (No Change)</option>
                  <option value="CS">CS</option>
                  <option value="CD">CD</option>
                  <option value="CI">CI</option>
                  <option value="CY">CY</option>
                  <option value="EC">EC</option>
                  <option value="ME">ME</option>
                </select>
                <select
                  value={bulkSemester}
                  onChange={(e) => setBulkSemester(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="NO_CHANGE">Sem (No Change)</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(sem => (
                    <option key={sem} value={`SEM_${sem}`}>S{sem}</option>
                  ))}
                </select>
                <select
                  value={bulkSection}
                  onChange={(e) => setBulkSection(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="NO_CHANGE">Sec (No Change)</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                  <option value="N/A">N/A</option>
                </select>
                <select
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="NO_CHANGE">Status (No Change)</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
                <select
                  value={bulkEmailVerified}
                  onChange={(e) => setBulkEmailVerified(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="NO_CHANGE">Email (No Change)</option>
                  <option value="VERIFIED">Email Verified</option>
                  <option value="NOT_VERIFIED">Email Not Verified</option>
                </select>
                <select
                  value={bulkPhoneVerified}
                  onChange={(e) => setBulkPhoneVerified(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                >
                  <option value="NO_CHANGE">Phone (No Change)</option>
                  <option value="VERIFIED">Phone Verified</option>
                  <option value="NOT_VERIFIED">Phone Not Verified</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Sort and Items Per Page */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-slate-700">Sort By:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              <option value="usn-asc">USN (A-Z)</option>
              <option value="usn-desc">USN (Z-A)</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="dept-asc">Department (A-Z)</option>
              <option value="dept-desc">Department (Z-A)</option>
                <option value="sem-asc">Semester (A-Z)</option>
                <option value="sem-desc">Semester (Z-A)</option>
                <option value="sec-asc">Section (A-Z)</option>
                <option value="sec-desc">Section (Z-A)</option>
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
            </select>
            {hasInactiveVisible && (
              <div className="flex items-center gap-3 text-sm font-bold text-slate-700">
                <span className="inline-block w-4 h-4 rounded-sm bg-red-100 border border-red-300"></span>
                <span>Inactive/Passed-out</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-slate-700">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    {canManageStudents && (
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="w-4 h-4 text-primary border-slate-300 rounded"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">USN</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Dept/Sem/Sec</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Verified</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Last Login</th>
                    {canManageStudents && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {getPaginatedStudents().map(student => (
                    <tr
                      key={student.id}
                      className={`hover:bg-slate-50 ${
                        student.status === 'INACTIVE' ? 'bg-red-50' : 'bg-white'
                      }`}
                    >
                      {canManageStudents && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedStudents.has(student.id)}
                            onChange={() => handleSelectStudent(student.id)}
                            className="w-4 h-4 text-primary border-slate-300 rounded"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{student.usn}</td>
                      <td className="px-4 py-3 text-sm text-slate-900">{student.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {(() => {
                          const emailUsername = student.email?.replace('@cambridge.edu.in', '');
                          const maxLength = 30; // length of "bijivemulayaswanthsimhareddy"
                          if (emailUsername && emailUsername.length > maxLength) {
                            return (
                              <span title={emailUsername}>
                                {emailUsername.substring(0, maxLength)}...
                              </span>
                            );
                          }
                          return emailUsername;
                        })()}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{student.phone || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-slate-900">
                        {student.department} / {student.semester?.replace('SEM_', 'S')} / {student.section || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="space-y-1">
                          <div className={student.isEmailVerified ? 'text-green-600' : 'text-slate-400'}>
                            {student.isEmailVerified ? '✓' : '✗'} Email
                          </div>
                          <div className={student.isPhoneVerified ? 'text-green-600' : 'text-slate-400'}>
                            {student.isPhoneVerified ? '✓' : '✗'} Phone
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {student.lastLoginAt ? (
                          <div>
                            <div>{new Date(student.lastLoginAt).toLocaleDateString()}</div>
                            <div className="text-slate-400">
                              {new Date(student.lastLoginAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400">Never</span>
                        )}
                      </td>
                      {canManageStudents && (
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditStudent(student)}
                              className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                              aria-label="Edit student"
                              title="Edit"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.862 4.487l1.687-1.688a1.5 1.5 0 112.121 2.122L7.5 18.091 3 19l.909-4.5L16.862 4.487z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteStudent(student.id)}
                              className="p-2 bg-red-600 text-white rounded hover:bg-red-700"
                              aria-label="Delete student"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5-4h4m-7 4h10" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {getPaginatedStudents().length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  {hasActiveFilters() ? 'No students match the current filters' : 'No students found'}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages} ({getSortedStudents().length} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 border rounded ${
                          currentPage === pageNum
                            ? 'bg-primary text-white border-primary'
                            : 'border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {canManageStudents && showEditModal && editingStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Edit Student</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">USN</label>
                <input
                  type="text"
                  value={editingStudent.usn}
                  onChange={(e) => setEditingStudent({...editingStudent, usn: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                <input
                  type="text"
                  value={editingStudent.name}
                  onChange={(e) => setEditingStudent({...editingStudent, name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  value={editingStudent.email}
                  onChange={(e) => setEditingStudent({...editingStudent, email: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                <input
                  type="text"
                  value={editingStudent.phone || ''}
                  onChange={(e) => setEditingStudent({...editingStudent, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Department</label>
                <select
                  value={editingStudent.department}
                  onChange={(e) => setEditingStudent({...editingStudent, department: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="CS">CS</option>
                  <option value="CD">CD</option>
                  <option value="CI">CI</option>
                  <option value="CY">CY</option>
                  <option value="EC">EC</option>
                  <option value="ME">ME</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Semester</label>
                <select
                  value={editingStudent.semester}
                  onChange={(e) => setEditingStudent({...editingStudent, semester: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(sem => (
                    <option key={sem} value={`SEM_${sem}`}>Semester {sem}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Section</label>
                <select
                  value={editingStudent.section || ''}
                  onChange={(e) => setEditingStudent({...editingStudent, section: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="">Select Section</option>
                  {['A', 'B', 'C', 'D'].map(section => (
                    <option key={section} value={section}>{section}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                <select
                  value={editingStudent.status}
                  onChange={(e) => setEditingStudent({...editingStudent, status: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingStudent.isEmailVerified}
                    onChange={(e) => setEditingStudent({...editingStudent, isEmailVerified: e.target.checked})}
                    className="w-4 h-4 text-primary border-slate-300 rounded"
                  />
                  <span className="text-sm text-slate-700">Email Verified</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editingStudent.isPhoneVerified}
                    onChange={(e) => setEditingStudent({...editingStudent, isPhoneVerified: e.target.checked})}
                    className="w-4 h-4 text-primary border-slate-300 rounded"
                  />
                  <span className="text-sm text-slate-700">Phone Verified</span>
                </label>
              </div>
            </div>

            {/* Password Reset Section */}
            <div className="mb-6 border-t border-slate-200 pt-6">
              <button
                type="button"
                onClick={() => setShowResetPassword(!showResetPassword)}
                className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium mb-4"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                {showResetPassword ? 'Hide' : 'Reset'} Password
              </button>

              {showResetPassword && (
                <div className="space-y-4 bg-orange-50 p-4 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password (min 6 characters)"
                        className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                      >
                        {showNewPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Confirm Password</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                      >
                        {showConfirmPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={resettingPassword}
                    className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {resettingPassword ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStudent}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminStudentManagement;
